<template>
  <div class="o-card--listview-container">
    <a :href="item.url" @click="onClick()">
      <article class="o-card o-card--listview o-card--listing o-card--clickable o-card--shadow-small">
        <div class="o-card__labels">
          <span v-if="showStatusLabel" :class="['c-object-label ' + showLabelClass]">
            {{ statusLabel }}
            <!-- Todo add label 'New' -->
          </span>
          <span v-if="showOpenHouseLabel" class="c-object-label c-object-label--secondary">
            {{ $label('openHouseLabel') }}:
            <time :datetime="item.upcomingeventdate">{{ openHouseDate }}</time>
          </span>
        </div>
        <div class="o-card--listview-image">
          <PictureAtom :isLazy="listingType === 'map' ? false : true" :src="picture" :sources="pictureSources"
            :width="285" :height="138" :alt="item.alt" />
        </div>
        <div class="o-card--listview-content">
          <div class="o-card__inner o-reorder-column">
            <div class="o-card__top o-reorder-column">
              <div class="u-padding o-reorder-column">
                <h3 class="h4 u-margin-bottom-none">
                  <span v-html="tagline"></span>
                </h3>
                <div class="o-card--listview-price">
                  <b class="h5">{{ item.price }}</b>
                  <span>{{ $label('perMonthLabel') }}</span>
                </div>
                <div class="o-reorder-column__first">
                  <strong class="u-heading u-margin-bottom-none">{{
                    item.city
                  }}
                  </strong>
                </div>
              </div>
              <div class="o-card__top-bottom">
                <div class="u-milli u-padding u-padding-bottom-small u-padding-top-small u-module">
                  <div class="o-card__innerlabels">
                    <span v-if="item.ageFrom === 50" class="c-object-label">
                      {{ $label('onlyFiftyPlusLabel') }}
                    </span>
                    <span v-if="item.ageFrom === 55" class="c-object-label">
                      {{ $label('onlyFiftyFivePlusLabel') }}
                    </span>
                    <span v-if="item.ageFrom === 65" class="c-object-label">
                      {{ $label('onlySixtyFivePlusLabel') }}
                    </span>
                    <span v-if="item.suitedForHomeSharers" class="c-object-label">
                      {{ $label('suitedForHomeSharersLabel') }}
                    </span>
                    <span v-if="item.onlyMiddleRent" class="c-object-label">
                      {{ $label('onlyMiddleRentLabel') }}
                    </span>
                    <span v-if="item.prioritizeKeyProfessions" class="c-object-label">
                      {{ $label('prioritizeKeyProfessionsLabel') }}
                    </span>

                    <span v-if="item.priorityArrangement" class="c-object-label">
                      {{ $label('priorityArrangementLabel') }}
                    </span>
                  </div>

                  <ul class="o-layout o-layout-gap o-layout--gutter-tiny">
                    <li class="o-layout__cell o-layout__cell--fill" v-if="item.size">
                      {{ $label('sizeLabel') }} <b>{{ item.size }} m²</b>
                    </li>
                    <li class="o-layout__cell o-layout__cell--fill" v-if="item.numberOfBedRooms">
                      {{ $label('roomsLabel') }}
                      <b> {{ item.numberOfBedRooms }} </b>
                    </li>
                    <li class="o-layout__cell o-layout__cell--fill" v-if="item.entitysubtypelabel">
                      {{ item.entitysubtypelabel }}
                    </li>
                  </ul>
                </div>

                <hr class="u-margin-bottom-none" />
              </div>
            </div>

            <div class="o-card__bottom">
              <ButtonAtom element-type="div" :href="item.url" modifier="c-button--hyperlink"
                :label="$label('viewUnitLabel')" />
            </div>
          </div>
        </div>
      </article>
    </a>
    <button class="o-button-clean o-card__btn-favorites" :class="isActive ? 'is-active' : null"
      :title="$label('addToFavoritesLabel')" @click="onClickSubscribe" v-if="item.status === 1"
      data-cy="results-add-to-favorites">
      <span class="u-visually-hidden">{{ $label('addToFavoritesLabel') }}</span>
      <svg width="50" height="50" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M13 21.5c0-4 2.5-6.5 6.5-6.5 2 0 3.834 1 5.5 3 1.576-1.746 3.634-3 5.5-3 4 0 6.5 2.5 6.5 6.5S33 30 25.002 35C17 30 13 25.5 13 21.5z"
          fill="none" stroke-width="2" stroke="#fff" />
      </svg>
    </button>
  </div>
</template>

<script>
import Constants from '@/constants.js'
import PictureAtom from '@/components/Utilities/Picture.vue'
import ButtonAtom from '@/components/Utilities/Button.vue'
import { mapState } from 'vuex'

export default {
  name: 'CardListing',
  components: {
    PictureAtom,
    ButtonAtom,
  },
  props: {
    listheader: {
      type: String,
      default: '' // Empty when there is no listheader
    },
    item: Object,
    indexnr: Number,
    listingType: {
      type: String,
      default: 'lijst',
    },
  },
  computed: {
    openHouseDate() {
      const dateObj = new Date(this.item.upcomingeventdate)
      const dateFormat = new Intl.DateTimeFormat(global.vesteda.culture, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
      return dateFormat.format(dateObj)
    },
    tagline() {
      return (
        this.item.street +
        ' ' +
        this.item.houseNumber +
        (this.item.houseNumberAddition ? this.item.houseNumberAddition : '')
      )
    },
    picture() {
      if (this.item.imageSmall && this.item.imageSmall !== '') {
        return this.item.imageSmall
      }

      return ''
    },
    pictureSources() {
      var sources = []
      if (this.item.imageSmall && this.item.imageSmall !== '') {
        sources.push({
          url: this.item.imageSmall,
          size: 285,
        })
      }
      if (this.item.imageBig && this.item.imageBig !== '') {
        sources.push({
          url: this.item.imageBig,
          size: 570,
        })
      }

      return sources
    },
    showStatusLabel() {
      return this.item.status !== Constants.UnitStatus.ForRent
    },
    showLabelClass() {
      let labelclass = ''
      /* Label 'New' not available */
      switch (this.item.status) {
        case Constants.UnitStatus.Rented:
          labelclass = 'c-object-label--rented'
          break
        case Constants.UnitStatus.RentedWithReservation:
          labelclass = 'c-object-label--rentedwithrestrictions'
          break
        case Constants.UnitStatus.Reserved:
          labelclass = 'c-object-label--reserved'
          break
      }
      return labelclass
    },
    showOpenHouseLabel() {
      return this.item.upcomingeventdate
    },
    statusLabel() {
      let label = ''

      switch (this.item.status) {
        case Constants.UnitStatus.Rented:
          label = this.$label('rentedLabel')
          break
        case Constants.UnitStatus.RentedWithReservation:
          label = this.$label('rentedWithReservationLabel')
          break
        case Constants.UnitStatus.OpenHouse:
          label = this.$label('openHouseLabel')
          break
        case Constants.UnitStatus.Reserved:
          label = this.$label('reservedLabel')
          break
      }
      return label
    },
    ...mapState({
      unitTypes: (state) => state.unitTypes,
      // todo: check if this is handy
      // unitType: state =>
      //   state.unitTypes.find(type => type.id === this.item.entitysubtypeid)
      activeSubscriptions: (state) => state.subscriptions,
    }),
    unitTypeLabel() {
      const unitType = this.unitTypes.find(
        (type) => type.id === this.item.entitysubtypeid
      )
      if (!unitType) {
        return null
      }
      return unitType.label
    },
    isActive() {
      // see if unit id is in localstorage
      return this.activeSubscriptions.some(
        (sub) => sub.id === this.item.entityguid
      )
    },
  },
  methods: {
    onMouseOver(item) {
      item.isActive = true
    },
    onMouseOut(item) {
      item.isActive = false
    },
    onClick() {
      this.$emit('gtmDataLayer', this)
    },
    onClickSubscribe() {

      // Set characteristics
      let characteristics = [];
      if (this.item.onlyMiddleRent) characteristics.push("middenhuur");
      if (this.item.ageFrom == '50') characteristics.push("50+");
      if (this.item.ageFrom == '55') characteristics.push("55+");
      if (this.item.ageFrom == '65') characteristics.push("65+");
      if (this.item.suitedForHomeSharers) characteristics.push("woningdelen");
      if (this.item.prioritizeKeyProfessions) characteristics.push("sleutelberoepen");
      (characteristics.length > 0) ? characteristics = characteristics.join(',') : characteristics = "";


      // fire custom event for plainjs module
      const event = new CustomEvent('toggleSubscription', {
        detail: {
          location: this.item.location, // Location
          feature: characteristics,
          id: this.item.entityguid,
          name: this.tagline,
          city: this.item.city,
          price: this.item.priceUnformatted,
          url: this.item.url,
          imageurl: this.item.imageSmall,
          suitedForHomeSharers: this.item.suitedForHomeSharers,
          availableFrom: this.item.availableFrom,
          subtype: this.unitTypeLabel,
          status: this.item.status,
          objectType: this.item.entitytypeid, // 1, unit
          unitId: this.item.entityguid,
          projectId: this.item.complexguid,
          projectName: this.item.complex,
        },
      })
      window.dispatchEvent(event)
    },
  },
}
</script>
